import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const toggleRow = (current: ReadonlySet<number>, index: number): ReadonlySet<number> => {
    const next = new Set(current);

    if (next.has(index)) {
        next.delete(index);
    } else {
        next.add(index);
    }

    return next;
};

const toggleAll = (current: ReadonlySet<number>, total: number): ReadonlySet<number> =>
    current.size === total
        ? new Set<number>()
        : new Set(Array.from({ length: total }, (_, i) => i));

const DataTable: FunctionComponent<Props> = ({
    ariaLabel,
    className = '',
    columns,
    onRowClick,
    onSelectionChange,
    role,
    rows,
    selectable,
    selectedRow,
    selectedRows = new Set<number>(),
    testId = TEST_ID
}) => {
    const allSelected = selectable && rows.length > 0 && selectedRows.size === rows.length;

    return (
        <table
            aria-label={ariaLabel}
            className={`${styles.dataTable} ${className}`}
            data-testid={testId}
            role={role}
        >
            <thead className={styles.header}>
                <tr>
                    {selectable ? (
                        <th className={`${styles.headerCell} ${styles.checkboxCell}`}>
                            <input
                                onChange={() => {
                                    onSelectionChange?.(toggleAll(selectedRows, rows.length));
                                }}
                                checked={!!allSelected}
                                className={styles.checkbox}
                                type="checkbox"
                            />
                        </th>
                    ) : null}

                    {columns.map(col => (
                        <th
                            className={styles.headerCell}
                            key={col.key}
                            style={col.width ? { width: col.width } : undefined}
                        >
                            {col.label}
                        </th>
                    ))}
                </tr>
            </thead>

            <tbody>
                {rows.map((row, rowIndex) => (
                    <tr
                        className={`${styles.bodyRow}${onRowClick ? ` ${styles.clickable}` : ''}${selectedRow === rowIndex ? ` ${styles.selected}` : ''}${selectable && selectedRows.has(rowIndex) ? ` ${styles.selected}` : ''}`}
                        key={rowIndex}
                        onClick={() => onRowClick?.(row, rowIndex)}
                    >
                        {selectable ? (
                            <td className={`${styles.bodyCell} ${styles.checkboxCell}`}>
                                <input
                                    onChange={() => {
                                        onSelectionChange?.(toggleRow(selectedRows, rowIndex));
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                    }}
                                    checked={selectedRows.has(rowIndex)}
                                    className={styles.checkbox}
                                    type="checkbox"
                                />
                            </td>
                        ) : null}

                        {columns.map(col => (
                            <td className={styles.bodyCell} key={col.key}>
                                {row[col.key]}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default DataTable;
